/// <amd-module name="Repos/PullRequestActions/PullRequestActionsService" />
import * as React from "react";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import {
    GitChange,
    GitItemDescriptor,
    GitItemRequestData,
    GitPullRequestMergeStrategy,
    GitPush,
    GitRefUpdate,
    ItemContentType,
    ShareNotificationContext,
    VersionControlChangeType,
    VersionControlRecursionType
} from "Repos/Common/Contracts/Git";
import { MergeOptionsDeleteSourceCheckboxMode, MergeOptionsTransitionWorkItemsCheckboxMode } from "Repos/Common/Contracts/VersionControlViewModel";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor, commitToVersionDescriptor, getVersionString } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import * as Resources from "Repos/PullRequestActions/Resources";
import {
    IChangeTargetBranchOptions,
    IPullRequestActionsService,
    IShowCompletePanelOptions
} from "Repos/Views/PullRequest/Contracts/PullRequestActions";
import { getPullRequestDataService } from "Repos/Views/PullRequest/PullRequestDataService";
import { getPullRequestPolicyService } from "Repos/Views/PullRequest/PullRequestPolicyService";
import { getPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import { getBlockingPolicies } from "Repos/Views/PullRequest/Util/Policy";
import {
    ICommitChangesContext,
    IPullRequestCommitChangesDetails,
    PullRequestCommitChangesPanel,
    PushCompleteListener
} from "Repos/PullRequestActions/Components/PullRequestCommitChangesPanel";
import { SharePullRequestPanel } from "Repos/PullRequestActions/Components/SharePullRequestPanel";
import { WorkItemTransitionWarning } from "Repos/PullRequestActions/Contracts/WorkItemTransitionWarning";
import { getGitClient } from "Repos/PullRequestActions/RestClient/Git";
import { getGraphClient, IGraphRestClient } from "Repos/PullRequestActions/RestClient/Graph";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Services, VssService } from "VSS/Platform/Context";
import * as VS_Graph from "VSS/Platform/Contracts/Graph";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { ChangeTargetBranchPanel } from "./Components/ChangeTargetBranchPanel";
import { ChecksPanel } from "./Components/ChecksPanel";
import { CompletePullRequestPanel } from "./Components/CompletePullRequestPanel";

class PullRequestActionsService extends VssService implements IPullRequestActionsService {
    private transitionWarningMessage = new ObservableValue<WorkItemTransitionWarning | undefined>(undefined);
    public changeTargetBranch(options: IChangeTargetBranchOptions): void {
        const errorMessage = new ObservableValue("");
        const saving = new ObservableValue(false);

        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <ChangeTargetBranchPanel
                    errorMessage={errorMessage}
                    onDismiss={async result => {
                        if (!result) {
                            onDismiss();
                            return;
                        }

                        saving.value = true;
                        try {
                            await options.changeTargetBranch(result.targetBranchName);
                        } catch (e) {
                            errorMessage.value = e.message;
                            saving.value = false;
                            return;
                        }

                        onDismiss();
                    }}
                    originalTargetBranchName={options.originalTargetBranchName}
                    repository={options.repository}
                    showOverlay={saving}
                    sourceBranchName={options.sourceBranchName}
                />
            );
        });
    }

    public commitLocalChanges(): void {
        const errorMessage = new ObservableValue("");
        const showOverlay = new ObservableValue(false);

        const prViewService = getPullRequestViewService(this.pageContext);
        const filesService = this.pageContext.getService<IGitFilesService>("IGitFilesService");
        const navigationService = getGitNavigationService(this.pageContext);

        const sourceBranchName = prViewService.getSourceBranch();
        const localChanges = prViewService.getLocalChanges();

        const listeners: PushCompleteListener[] = [];
        const context: ICommitChangesContext = {
            addPushCompleteListener: (listener: PushCompleteListener): void => {
                listeners.push(listener);
            }
        };

        this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissPanel = async (details: IPullRequestCommitChangesDetails | undefined) => {
                if (details) {
                    errorMessage.value = "";
                    showOverlay.value = true;

                    let newBranchName: string | undefined;
                    if (sourceBranchName !== details.branch) {
                        newBranchName = details.branch;
                    }

                    let push: GitPush;
                    try {
                        push = await this.pushChanges(details, !!newBranchName);
                    } catch (ex) {
                        showOverlay.value = false;
                        errorMessage.value = ex.message;
                        return;
                    }

                    const promises: Promise<void>[] = [];
                    for (const listener of listeners) {
                        const postPushResult = listener(push, newBranchName);
                        if (postPushResult && postPushResult.then) {
                            promises.push(postPushResult);
                        }
                    }

                    let error: Error | undefined;
                    if (promises.length) {
                        try {
                            await Promise.all(promises);
                        } catch (ex) {
                            error = ex;
                        }
                    }

                    // Clear the local pending changes
                    if (!newBranchName) {
                        localChanges.updateVersion(commitToVersionDescriptor(push.commits[0].commitId));
                    }

                    // Dimiss the panel
                    onDismiss();

                    // Show a message about what we just pushed
                    const buttons: IButtonProps[] = [];
                    let message: React.ReactNode;

                    const routingService = getReposRoutingService(this.pageContext);
                    const repository = prViewService.getSourceRepository();
                    const commit = push.commits[0];

                    const commitUrl = routingService.getCommitUrl(
                        repository.name,
                        repository.project.name,
                        commit.commitId,
                        branchNameToRef(details.branch)
                    );
                    const commitLink = (
                        <span>
                            <Icon iconName="BranchCommit" className="margin-right-4" />
                            <FPSLink href={commitUrl}>{commit.commitId.substr(0, 8) + ": " + getShortCommitComment(commit.comment)}</FPSLink>
                        </span>
                    );

                    if (newBranchName) {
                        const newBranchUrl = routingService.getFilesUrl(
                            repository.name,
                            repository.project.name,
                            undefined,
                            branchToVersionDescriptor(newBranchName)
                        );
                        message = (
                            <FormatComponent elementType="span" format={Resources.CommittedToNewBranchFormat}>
                                <span>
                                    <Icon iconName="OpenSource" className="margin-right-4" />
                                    <FPSLink href={newBranchUrl}>{newBranchName}</FPSLink>
                                </span>
                                {commitLink}
                            </FormatComponent>
                        );

                        const pullRequestUrl = routingService.getPullRequestCreateUrl(
                            repository.name,
                            repository.project.name,
                            details.branch,
                            sourceBranchName,
                            repository.id,
                            repository.id
                        );

                        buttons.push({
                            href: pullRequestUrl,
                            text: Resources.CreateNewPullRequest,
                            onClick: event => {
                                onClickFPS(this.pageContext, pullRequestUrl, true, event);
                            }
                        });
                    } else {
                        message = (
                            <FormatComponent elementType="span" format={Resources.CommittedToCurrentPullRequestFormat}>
                                {commitLink}
                            </FormatComponent>
                        );
                    }

                    const messagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
                    messagesService.showMessage({
                        buttons,
                        renderMessage: () => (
                            <>
                                {message}
                                {error ? <span>{error.message}</span> : null}
                            </>
                        ),
                        severity: error ? MessageCardSeverity.Warning : MessageCardSeverity.Info
                    });
                } else {
                    onDismiss();
                }
            };

            return (
                <ChangesContext.Provider value={{ filesService, navigationService }}>
                    <PullRequestCommitChangesPanel
                        branchName={sourceBranchName}
                        changes={localChanges.getDirtyFiles().value}
                        changeVersionString={getVersionString(localChanges.version)}
                        comment={Resources.CommitInitialCommentFormat}
                        commitChangesContext={context}
                        errorMessage={errorMessage}
                        onDismiss={onDismissPanel}
                        showOverlay={showOverlay}
                    />
                </ChangesContext.Provider>
            );
        });
    }

    public revertLocalChanges(): void {
        showMessageDialog(this.pageContext, {
            lightDismiss: false,
            message: Resources.RevertConfirmation,
            okButtonProps: { danger: true, primary: false, text: Resources.Revert },
            onClose: (result: boolean): void => {
                if (result) {
                    const prViewService = getPullRequestViewService(this.pageContext);
                    prViewService.getLocalChanges().revertAll();
                }
            }
        });
    }

    public async showCompletePanel(options: IShowCompletePanelOptions): Promise<void> {
        const prViewService = getPullRequestViewService(this.pageContext);
        const permissionsService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");
        const canOverridePolicies = permissionsService.hasPermission(
            GitRepositoryPermissions.PullRequestBypassPolicy,
            branchToVersionDescriptor(prViewService.getTargetBranch().value)
        );

        const policyService = getPullRequestPolicyService(this.pageContext);
        let blockingPolicyMessage = "";

        blockingPolicyMessage = !options.isAutoComplete ? prViewService.getBlockingPolicyMessage() : "";

        const errorMessage = new ObservableValue("");
        const saving = new ObservableValue(false);
        this.transitionWarningMessage = prViewService.getWitTransitionWarningMessage();

        const { sourceRefStatus, preferences } = getPullRequestDataService(this.pageContext).getPullRequestBasicData().value;
        const vcViewModelService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const userPrefs = vcViewModelService.getUserPreferences();
        const description = prViewService.getDescription().value;
        const usePRDescriptionToUpdateWITStates = description && description.indexOf("#") != -1;
        const transitionWorkItemsPref = Boolean(
            !usePRDescriptionToUpdateWITStates &&
                preferences.witTransitionsSticky &&
                userPrefs.mergeOptionsTransitionWorkItemsCheckboxMode === MergeOptionsTransitionWorkItemsCheckboxMode.Checked
        );
        const deleteSourceBranchPref = userPrefs.mergeOptionsDeleteSourceCheckboxMode === MergeOptionsDeleteSourceCheckboxMode.Checked;

        let mergeStrategy =
            prViewService.getCompletionStatus()?.value?.completionOptions?.mergeStrategy ||
            userPrefs.mergeOptionsMergeStrategy ||
            GitPullRequestMergeStrategy.NoFastForward;

        // See if this pull request has any resolved conflicts. In this case, we may not be able
        // to use any merge options that involve rebase.
        let hasResolvedConflicts = false;
        try {
            hasResolvedConflicts =
                (
                    await getGitClient(this.pageContext).getPullRequestConflicts(
                        prViewService.getTargetRepository().id,
                        prViewService.getId(),
                        prViewService.getTargetRepository().project.id,
                        0,
                        1,
                        false,
                        false,
                        true
                    )
                ).length > 0;
        } catch {
            // Best effort
        }

        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <Observer warningMessage={this.transitionWarningMessage}>
                     {() => {
                       return (
                            <CompletePullRequestPanel
                                checks={policyService.getCheckEvaluations()}
                                commitCount={prViewService.getCommits().length}
                                completionStatus={prViewService.getCompletionStatus()}
                                blockingPolicyMessage={blockingPolicyMessage}
                                canOverridePolicies={canOverridePolicies}
                                disallowedMergeStrategies={policyService.getDisallowedMergeStrategies()}
                                errorMessage={errorMessage}
                                warningMessage={this.transitionWarningMessage}
                                hasResolvedConflicts={hasResolvedConflicts}
                                initialOptions={{
                                    completeWorkItems: transitionWorkItemsPref,
                                    deleteBranch: deleteSourceBranchPref,
                                    mergeCommitDescription: prViewService.getDescription().value || "",
                                    mergeCommitTitle: format(Resources.MergeCommitTitle, prViewService.getId(), prViewService.getTitle().value),
                                    mergeStrategy
                                }}
                                isAutoComplete={options.isAutoComplete}
                                onDismiss={async completionOptions => {
                                    if (!completionOptions) {
                                        onDismiss();
                                        return;
                                    }

                                    saving.value = true;
                                    try {
                                        await options.onComplete(completionOptions);
                                    } catch (e) {
                                        errorMessage.value = e.message;
                                        saving.value = false;
                                        return;
                                    }

                                    if (
                                        completionOptions.deleteBranch !== deleteSourceBranchPref ||
                                        completionOptions.completeWorkItems !== transitionWorkItemsPref ||
                                        completionOptions.mergeStrategy !== mergeStrategy
                                    ) {
                                        userPrefs.mergeOptionsTransitionWorkItemsCheckboxMode = completionOptions.completeWorkItems
                                            ? MergeOptionsTransitionWorkItemsCheckboxMode.Checked
                                            : MergeOptionsTransitionWorkItemsCheckboxMode.Unchecked;
                                        if (!prViewService.getSourceDeleteBlockReason()){
                                            userPrefs.mergeOptionsDeleteSourceCheckboxMode = completionOptions.deleteBranch
                                                ? MergeOptionsDeleteSourceCheckboxMode.Checked
                                                : MergeOptionsDeleteSourceCheckboxMode.Unchecked;
                                        }
                                        userPrefs.mergeOptionsMergeStrategy = completionOptions.mergeStrategy;

                                        vcViewModelService.setUserPreferences(userPrefs);
                                    }

                                    onDismiss();
                                }}
                                showOverlay={saving}
                                sourceBranchName={options.sourceBranchName}
                                sourceRefStatus={sourceRefStatus}
                            />
                        ); 
                     }}
                </Observer>
            );
        });
    }

    public share(): void {
        this.sharePullRequest();
    }

    public showChecksPanel(): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return <ChecksPanel onDismiss={onDismiss} />;
        });
    }

    private async pushChanges(details: IPullRequestCommitChangesDetails, isNewBranch: boolean): Promise<GitPush> {
        const prViewService = getPullRequestViewService(this.pageContext);
        const repository = prViewService.getSourceRepository();
        const localChanges = prViewService.getLocalChanges();
        const dirtyFiles = localChanges.getDirtyFiles().value;

        const changes: GitChange[] = [];
        for (const localChange of dirtyFiles) {
            changes.push({
                changeType: VersionControlChangeType.Edit,
                item: {
                    path: localChange.path
                },
                newContent: {
                    content: localChange.getText().value,
                    contentType: ItemContentType.RawText
                }
            } as GitChange);
        }

        const push = {
            commits: [
                {
                    changes,
                    comment: details.comment
                }
            ],
            refUpdates: [
                {
                    name: "refs/heads/" + details.branch,
                    oldObjectId: localChanges.version.version
                } as GitRefUpdate
            ]
        } as GitPush;

        const client = getGitClient(this.pageContext);

        let result: GitPush;
        try {
            result = await client.createPush(push, repository.id);
        } catch (ex) {
            if (ex.status === 409) {
                // The branch has been updated since this item was first loaded, get the most-recent version of each file
                // to see if it has changed itself since then.
                let retryCommitId: string | undefined;

                // Fetch the item itself to make sure it hasn't changed from what was
                // originally edited
                try {
                    const latestItems = await client.getItemsBatch(
                        {
                            itemDescriptors: dirtyFiles.map(file => {
                                return {
                                    path: file.path,
                                    recursionLevel: VersionControlRecursionType.None,
                                    ...branchToVersionDescriptor(prViewService.getSourceBranch())
                                } as GitItemDescriptor;
                            })
                        } as GitItemRequestData,
                        repository.id,
                        repository.project.id
                    );

                    if (latestItems) {
                        let filesChanged = false;
                        for (let i = 0; i < dirtyFiles.length; i++) {
                            const originalContent = await dirtyFiles[i].getOriginalItemContent();
                            if (!latestItems[i] || latestItems[i][0].objectId !== originalContent.details.objectId) {
                                filesChanged = true;
                                break;
                            }
                        }
                        if (!filesChanged) {
                            retryCommitId = latestItems[0][0].commitId;
                        }
                    }
                } catch {
                    // Best effort -- we'll throw the original push exception in this case.
                }

                if (retryCommitId) {
                    // Even though the branch was updated, this item was not touched.
                    // Retry the push using the most recent commit id
                    push.refUpdates[0].oldObjectId = retryCommitId;
                    result = await client.createPush(push, repository.id);
                } else {
                    // Throw the conflict error
                    if (isNewBranch) {
                        throw ex;
                    } else {
                        throw new Error(format(Resources.ConflictsOnPullRequestPush, details.branch));
                    }
                }
            } else {
                // Non-conflict error
                throw ex;
            }
        }

        return result;
    }

    private async sharePullRequest(): Promise<void> {
        const errorMessage = new ObservableValue("");
        const saving = new ObservableValue(false);

        const prViewService = getPullRequestViewService(this.pageContext);
        const peoplePickerProvider = new PeoplePickerProvider(this.pageContext);

        const reviewers = prViewService.getReviewers().value;
        const initialReviewers =
            reviewers.length > 0
                ? await peoplePickerProvider.getEntitiesFromUniqueAttribute(prViewService.getReviewers().value.map(x => x.identity.id))
                : [];

        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <SharePullRequestPanel
                    errorMessage={errorMessage}
                    initialReviewers={initialReviewers}
                    onClose={async result => {
                        if (!result) {
                            onDismiss();
                            return;
                        }

                        const targetRepository = prViewService.getTargetRepository();
                        const pullRequestId = prViewService.getId();

                        const identities = result.identityIds.map(identity => {
                            return { id: identity };
                        }) as IdentityRef[];

                        saving.value = true;
                        try {
                            await getGitClient(this.pageContext).sharePullRequest(
                                { message: result.message, receivers: identities } as ShareNotificationContext,
                                targetRepository.id,
                                pullRequestId,
                                targetRepository.project.id
                            );
                        } catch (e) {
                            errorMessage.value = e.message;
                            saving.value = false;
                            return;
                        }

                        onDismiss();
                    }}
                    peoplePickerProvider={peoplePickerProvider}
                    showOverlay={saving}
                    materializeIdentity={async identity => {
                        if (identity.entityType.trim().toLowerCase() === "group") {
                            const creationContext: VS_Graph.GraphGroupOriginIdCreationContext = {
                                originId: identity.originId
                            } as VS_Graph.GraphGroupOriginIdCreationContext;
                            const graphClient: IGraphRestClient = getGraphClient(this.pageContext);
                            return graphClient.createGroup(creationContext).then((graphGroup: VS_Graph.GraphGroup) => {
                                return graphClient.getStorageKey(graphGroup.descriptor).then((storageKey: VS_Graph.GraphStorageKeyResult) => {
                                    identity.localId = storageKey.value;
                                    return identity;
                                });
                            });
                        }

                        const creationContext: VS_Graph.GraphUserOriginIdCreationContext = {
                            originId: identity.originId
                        } as VS_Graph.GraphUserOriginIdCreationContext;
                        const graphClient: IGraphRestClient = getGraphClient(this.pageContext);
                        return graphClient.createUser(creationContext).then((graphUser: VS_Graph.GraphUser) => {
                            return graphClient.getStorageKey(graphUser.descriptor).then((storageKey: VS_Graph.GraphStorageKeyResult) => {
                                identity.localId = storageKey.value;
                                return identity;
                            });
                        });
                    }}
                />
            );
        });
    }
}

Services.add("IPullRequestActionsService", { serviceFactory: PullRequestActionsService });
